import { DynamoDBClient, QueryCommand } from "@aws-sdk/client-dynamodb";
import { unmarshall } from "@aws-sdk/util-dynamodb";
import { useQuery } from "@tanstack/vue-query";
import { ComputedRef } from "vue";
import type { Event } from "../../lib/dynamodb/types";

/**
 * Get all events for a project using GSI_events_by_project
 */
export async function getProjectEvents(
	projectId: string,
	dynamodb: { user_table_name: string; client: DynamoDBClient },
): Promise<Event[]> {
	const { client, user_table_name } = dynamodb;
	const events: Event[] = [];

	const command = new QueryCommand({
		TableName: user_table_name,
		IndexName: "GSI_events_by_project",
		KeyConditionExpression: "#gsiPk = :pk",
		ProjectionExpression:
			"PK, event_name, project_id, event_start, event_end, #tz, created_at, updated_at",
		ExpressionAttributeNames: {
			"#gsiPk": "GSI_events_by_project_PK",
			"#tz": "timezone",
		},
		ExpressionAttributeValues: {
			":pk": { S: `PROJECT#${projectId}` },
		},
	});

	try {
		const result = await client.send(command);
		if (result.Items) {
			for (const item of result.Items) {
				const unmarshalled = unmarshall(item);
				const pk = unmarshalled.PK as string;
				const eventIdMatch = pk.match(/EVENT#(.+)$/);
				const eventId = eventIdMatch ? eventIdMatch[1] : "";

				if (eventId) {
					events.push({
						event_id: eventId,
						event_name: unmarshalled.event_name || eventId,
						project_id: unmarshalled.project_id || projectId,
						event_start: unmarshalled.event_start || 0,
						event_end: unmarshalled.event_end || 0,
						timezone: unmarshalled.timezone,
						created_at: unmarshalled.created_at || 0,
						updated_at: unmarshalled.updated_at || 0,
					});
				}
			}
		}
	} catch (err) {
		throw err;
	}

	return events;
}

export function useProjectEvents(
	projectId: ComputedRef<string | null | undefined>,
	dynamodb: ComputedRef<{
		user_table_name: string;
		cam_table_name?: string;
		client: DynamoDBClient;
	} | null>,
	credentialsReset: ComputedRef<number>,
) {
	return useQuery({
		queryKey: ["project-events", projectId, credentialsReset],
		queryFn: ({ queryKey: [_, pId] }) =>
			getProjectEvents(
				pId as string,
				dynamodb.value as { user_table_name: string; client: DynamoDBClient },
			),
		enabled: () => !!projectId.value && !!dynamodb.value,
	});
}
